import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { recordPaymentRequestWaiver, confirmPaperworkDocument, getPaymentHolds, revokePaymentRequestWaiver } from '@/services/paperwork.service.js';
import CarrierDocAttach, { ledgerRowName } from '@/components/account/CarrierDocAttach.jsx';
import { formatHumanDate } from '@/lib/date-display.js';

// THE PAPERWORK HOLDS AS A BLOCK (paperwork-1 D9, D11, L2-2; screens 4 and 5). Every word and every state is the server's
// (services/payment-holds.service.js, the one function; GET /api/tracon/deals/:dealId/payment-holds, or a 409 refusal's body):
// this file derives nothing. Each block names the record, says what the switch does with it (hold refuses the broker's send,
// notify sends and flags the deal), and carries the button that resolves it.

const TONE = {
  hold: 'rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800',
  notify: 'rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800',
  waived: 'rounded-md border border-sky-300 bg-sky-50 p-3 text-sm text-sky-800',
  unknown: 'rounded-md border border-border bg-bg p-3 text-sm text-text-muted',
};
const day = (v) => (v ? String(v).slice(0, 10) : '');

/** "Send payment request without a signed application": the reason field and its button, owner or producer only. */
export function WaiverForm({ dealId, policyLabel, canWaive, canWaiveWhy, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!canWaive) return <p className="mt-2 text-xs" data-waiver-unavailable>{canWaiveWhy || 'Only the owner or a producer may record a waiver.'}</p>;
  if (!open) {
    return (
      <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setOpen(true)} data-action="open_waiver">
        Send payment request without a signed application
      </Button>
    );
  }
  async function save() {
    if (!reason.trim()) { setError('Say why: the reason is required and is logged.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await recordPaymentRequestWaiver(dealId, reason.trim());
      toast.success(`Waiver recorded for ${policyLabel}: ${r?.waiver?.reason || reason.trim()}`);
      setOpen(false); setReason('');
      onRecorded?.(r?.waiver || null);
    } catch (err) {
      setError(err?.body?.error || err?.message || 'The waiver was not recorded');
    } finally { setBusy(false); }
  }
  return (
    <div className="mt-2 space-y-2" data-waiver-form>
      <label className="block text-xs font-medium">
        Why may this payment request go without a signed application? (logged with your name)
        <textarea className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-waiver-reason autoFocus />
      </label>
      {error ? <p className="text-xs text-danger" data-waiver-error>{error}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" disabled={busy || !reason.trim()} onClick={save} data-action="record_waiver">{busy ? 'Recording...' : `Record the waiver for ${policyLabel}`}</Button>
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => { setOpen(false); setError(null); }}>Cancel</Button>
      </div>
    </div>
  );
}

/**
 * THE WAIVER ON THE PAPERWORK TAB (round 2, David Q5: "a waiver lasts to the end of its term; owner can revoke it mid-term
 * (logged, reason)"). Reads the server's holds preview (its live waiver with ends_on, and can_revoke); renders nothing when the
 * deal has no live waiver. The owner revokes it here with a reason; the result is shown in place (and the toast names it).
 */
export function WaiverStanding({ dealId, policyLabel }) {
  const [holds, setHolds] = useState(null);
  const [readError, setReadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [revoked, setRevoked] = useState(null);
  const load = useCallback(() => {
    if (!dealId) return;
    getPaymentHolds(dealId).then((r) => { setHolds(r); setReadError(null); }).catch((e) => setReadError(e?.body?.error || e?.message || 'the holds could not be read'));
  }, [dealId]);
  useEffect(() => { load(); }, [load]);
  if (revoked) {
    return (
      <div className={TONE.unknown} data-waiver-revoked={revoked.id}>
        Waiver revoked{revoked.revoked_at ? ` on ${formatHumanDate(revoked.revoked_at)}` : ''}: "{revoked.revoked_reason}". Payment requests on {policyLabel} need the signed application in the window again.
      </div>
    );
  }
  if (readError) {
    return (
      <div className={TONE.unknown} data-waiver-standing="unread">
        Could not check the waiver on {policyLabel}: {readError}.{' '}
        <Button type="button" size="sm" variant="outline" className="ml-1" onClick={load}>Read the waiver again</Button>
      </div>
    );
  }
  if (holds?.waiver_unread) return <div className={TONE.unknown} data-waiver-standing="unread">Could not check the waiver on {policyLabel}: {holds.waiver_unread}.</div>;
  const w = holds?.waiver;
  if (!w) return null;
  async function revoke() {
    if (!reason.trim()) { setError('Say why: the reason is required and is logged.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await revokePaymentRequestWaiver(dealId, w.id, reason.trim());
      toast.success(`Waiver revoked for ${policyLabel}: ${r?.waiver?.revoked_reason || reason.trim()}`);
      setRevoked(r?.waiver || { id: w.id, revoked_reason: reason.trim() });
    } catch (err) {
      setError(err?.body?.error || err?.message || 'The waiver was not revoked');
    } finally { setBusy(false); }
  }
  return (
    <div className={TONE.waived} data-waiver-standing={w.id}>
      <strong>Payment requests on {policyLabel} may go without a signed application.</strong> Waiver recorded by {w.actor_label || w.actor_id || 'a producer'}{w.created_at ? ` on ${formatHumanDate(w.created_at)}` : ''}: "{w.reason}".{' '}
      {w.ends_on ? `It lasts until the term ends on ${formatHumanDate(w.ends_on)}.` : 'The term has no end date on record, so it stands until it is revoked.'}
      {!holds.can_revoke ? <p className="mt-2 text-xs" data-revoke-unavailable>{holds.can_revoke_why || 'Only the owner may revoke a waiver.'}</p> : null}
      {holds.can_revoke && !open ? (
        <div><Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setOpen(true)} data-action="open_revoke_waiver">Revoke the waiver for {policyLabel}</Button></div>
      ) : null}
      {holds.can_revoke && open ? (
        <div className="mt-2 space-y-2" data-revoke-form>
          <label className="block text-xs font-medium">
            Why is the waiver revoked? (logged with your name)
            <textarea className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-revoke-reason autoFocus />
          </label>
          {error ? <p className="text-xs text-danger" data-revoke-error>{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" disabled={busy || !reason.trim()} onClick={revoke} data-action="revoke_waiver">{busy ? 'Revoking...' : `Revoke the waiver for ${policyLabel}`}</Button>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => { setOpen(false); setError(null); }}>Cancel</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The signed application in the window. `finding` is the server's (a preview finding, or a refusal's hold); `door` says what the
 * broker is about to do ('payment': a payment request; 'paperwork': Signed paperwork received). onDrop opens the drop with the
 * file preselected Signed application.
 */
export function SignedApplicationHold({ finding, mode, door = 'payment', dealId, policyLabel, canWaive, canWaiveWhy, onDrop, onChanged }) {
  if (!finding || finding.state === 'clear') return null;
  const w = finding.window || {};
  if (finding.state === 'unknown') {
    return <div className={TONE.unknown} data-hold="signed_application" data-hold-state="unknown">{finding.sentence || w.status}. It will not be held; you are told instead.</div>;
  }
  const drop = finding.action ? (
    <Button type="button" size="sm" className="mt-2 mr-2" onClick={onDrop} data-action={finding.action.key}>{finding.action.label}</Button>
  ) : null;
  if (finding.waived) {
    return (
      <div className={TONE.waived} data-hold="signed_application" data-hold-state="waived">
        <strong>No signed application in the window.</strong> {w.status}. This request goes under the waiver {finding.waiver?.by || 'a producer'} recorded{finding.waiver?.at ? ` on ${day(finding.waiver.at)}` : ''}: "{finding.waiver?.reason}"{finding.waiver?.ends_on ? `, until the term ends on ${formatHumanDate(finding.waiver.ends_on)}` : ''}.
        <div>{drop}</div>
      </div>
    );
  }
  if (door === 'paperwork_recorded') {
    return (
      <div className={TONE.notify} data-hold="signed_application" data-hold-state="flagged">
        <strong>Flagged on the deal: signed paperwork was recorded on this bind, but no signed application is filed in the window.</strong> {w.status}.
        <div>{drop}</div>
      </div>
    );
  }
  const what = door === 'paperwork' ? 'Signed paperwork received' : 'This payment request';
  if (mode === 'hold') {
    return (
      <div className={TONE.hold} data-hold="signed_application" data-hold-state="held">
        <strong>Held: no signed application in the window.</strong> {w.status}. {what} waits until it is filed{door === 'payment' ? ', or until the owner or a producer records a waiver' : ''}.
        <div className="flex flex-wrap items-start">{drop}</div>
        {door === 'payment' ? <WaiverForm dealId={dealId} policyLabel={policyLabel} canWaive={canWaive} canWaiveWhy={canWaiveWhy} onRecorded={onChanged} /> : null}
      </div>
    );
  }
  return (
    <div className={TONE.notify} data-hold="signed_application" data-hold-state="notify">
      <strong>No signed application in the window.</strong> {w.status}. {door === 'paperwork' ? 'Signed paperwork received can still be recorded' : 'This payment request still goes'} (the payment holds are set to notify), and it is flagged on the deal.
      <div>{drop}</div>
    </div>
  );
}

/** The carrier's endorsement first: the endorsement's request waits for its document (hold) or goes with a warning (notify). */
export function EndorsementDocumentHold({ finding, mode, dealId, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!finding || finding.state === 'clear') return null;
  const t = finding.transaction || {};
  if (finding.state === 'unknown') return <div className={TONE.unknown} data-hold="endorsement_document" data-hold-state="unknown">{finding.sentence}. It will not be held; you are told instead.</div>;
  const key = String(finding.action?.key || '');
  const button = key.startsWith('confirm_document:')
    ? (
      <Button type="button" size="sm" className="mt-2" disabled={busy} data-action="confirm_document" onClick={async () => {
        setBusy(true);
        try { const r = await confirmPaperworkDocument(dealId, key.split(':')[1]); toast.success(`${r?.document?.name || finding.document?.name || 'The endorsement'} is confirmed as this policy's`); onChanged?.(); }
        catch (err) { toast.error(err?.body?.error || err?.message || 'Not confirmed'); } finally { setBusy(false); }
      }}>{finding.action.label}: {finding.document?.name}</Button>
    )
    : <CarrierDocAttach dealId={dealId} txnId={t.id} rowName={ledgerRowName('endorsement', t.effective_date)} onAttached={onChanged} />;
  return (
    <div className={mode === 'hold' ? TONE.hold : TONE.notify} data-hold="endorsement_document" data-hold-state={mode === 'hold' ? 'held' : 'notify'}>
      <strong>{mode === 'hold' ? 'Held: the carrier\'s endorsement first.' : 'The carrier\'s endorsement is not on file.'}</strong> {finding.action?.label || finding.sentence}.
      {mode === 'hold' ? ' The endorsement\'s payment request waits until it is attached and confirmed.' : ' The request still goes (the payment holds are set to notify) and is flagged on the deal.'}
      <div>{button}</div>
    </div>
  );
}

/** An unpaid endorsement the request would not collect: named, never silently treated as a renewal. */
export function UnmatchedEndorsementHold({ finding, mode, onCollect }) {
  if (!finding || finding.state === 'clear') return null;
  if (finding.state === 'unknown') return <div className={TONE.unknown} data-hold="endorsement_unmatched" data-hold-state="unknown">{finding.sentence}. It will not be held; you are told instead.</div>;
  return (
    <div className={mode === 'hold' ? TONE.hold : TONE.notify} data-hold="endorsement_unmatched" data-hold-state={mode === 'hold' ? 'held' : 'notify'}>
      <strong>{mode === 'hold' ? 'Held: an unpaid endorsement this request does not collect.' : 'An unpaid endorsement this request does not collect.'}</strong>{' '}
      {finding.sentence.charAt(0).toUpperCase() + finding.sentence.slice(1)}.
      {finding.action && onCollect
        ? <div><Button type="button" size="sm" className="mt-2" onClick={() => onCollect(finding.action.key.split(':')[1])} data-action="collect_endorsement">{finding.action.label}</Button></div>
        : <span> Record the carrier's answer on Transaction history, then send the endorsement's own request.</span>}
    </div>
  );
}
